/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentProps } from 'preact';
import { useCallback, useState } from 'preact/hooks';
import { BundleType } from '../resources/nls/bundle';
import { ColorPicker } from '#UNSAFE_ColorPicker';
import { type CloseDetail as DropdownCloseDetail } from '#UNSAFE_Dropdown';
import { getClientHints } from '#utils/PRIVATE_clientHints';
import { FormFieldContext } from '../hooks/UNSAFE_useFormFieldContext';
import { InlineUserAssistance } from '../UNSAFE_UserAssistance';
import { dropdownStyles, userAssistanceStyles } from './themes/InputColorPickerDropdownStyles.css';
import { PositionData } from '../UNSAFE_Floating';
import { StickyPositioningDropdown } from '#PRIVATE_FormControls';
import { SectionedContent } from '#PRIVATE_FormControls';
import { type CloseDetail as SheetCloseDetail, Sheet } from '#UNSAFE_Sheet';
import type { TestIdProps } from '#hooks/UNSAFE_useTestId';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { SizeOptions } from '#UNSAFE_Floating/Floating';
import { InputColorPublicProps as InputColorProps } from './InputColor.types';

type StickyPositioningDropdownProps = ComponentProps<typeof StickyPositioningDropdown>;
type ColorPickerProps = ComponentProps<typeof ColorPicker>;
type PickedPropsFromColorPicker = Pick<ColorPickerProps, 'value' | 'onCommit'>;
type PickedPropsFromDropdown = Omit<StickyPositioningDropdownProps, 'onClose'>;
type CloseDetail = SheetCloseDetail | DropdownCloseDetail;
type PickedPropsFromInputColor = Pick<
  InputColorProps,
  | 'swatchSize'
  | 'palette'
  | 'value'
  | 'aria-describedby'
  | 'assistiveText'
  | 'columnSpan'
  | 'helpSourceLink'
  | 'helpSourceText'
  | 'isDisabled'
  | 'isReadonly'
  | 'isRequired'
  | 'isRequiredShown'
  | 'label'
  | 'labelEdge'
  | 'labelStartWidth'
  | 'messages'
  | 'textAlign'
  | 'testId'
  | 'userAssistanceDensity'
  | 'variant'
>;

type Props = PickedPropsFromColorPicker &
  PickedPropsFromInputColor &
  PickedPropsFromDropdown &
  TestIdProps & {
    /**
     * Called when the dropdown or sheet is supposed to be closed.
     */
    onClose?: (detail: CloseDetail) => void;
  };

/**
 * Component that renders ColorPicker either inside a dropdown or sheet; in mobile,
 * it renders inside a sheet. Additionally, when a dropdown is being shown beneath
 * the component, we also show any user assistance text or messages below the picker
 * because otherwise that information would be obscured from view. When a sheet is being
 * shown, it will always display UA and messages at the bottom of the sheet.
 */
const InputColorPickerDropdown = ({
  anchorRef,
  assistiveText,
  swatchSize,
  palette,
  helpSourceLink,
  helpSourceText,
  isDisabled,
  isOpen,
  isReadonly,
  label,
  onClose,
  onCommit,
  messages,
  testId,
  userAssistanceDensity,
  value
}: Props) => {
  // If the dropdown opens above the component, we don't want to show UA or messages.
  const [isDropdownAbove, setDropdownAbove] = useState(false);
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  const handleDropdownPosition = useCallback<
    NonNullable<StickyPositioningDropdownProps['onPosition']>
  >((data: PositionData) => {
    setDropdownAbove(data.placement.startsWith('top'));
  }, []);
  // Reset the flag when the dropdown is closed.
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen);
    if (!isOpen) {
      setDropdownAbove(false);
    }
  }

  const sizeOptions: SizeOptions = {
    isMaxWidthAdjusted: false,
    isMaxHeightAdjusted: true
  };

  // On mobile phones, the dropdown becomes a sheet.
  const shouldShowSheet = isPhone();

  const { colorPicker_label } = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const colorPickerAriaLabel = colorPicker_label();

  const colorPickerMainContent = (
    <ColorPicker
      height={!shouldShowSheet ? 'auto' : undefined}
      onCommit={onCommit}
      // for sheet, we want the width to grow to the width of the sheet, otherwise it will use the theme default.
      maxWidth={shouldShowSheet ? '100%' : undefined}
      width="100%"
      swatchSize={swatchSize}
      palette={palette}
      testId={testId}
      value={value}
    />
  );

  const inlineUserAssistanceFooter =
    isOpen &&
    (!isDropdownAbove || shouldShowSheet) &&
    !isDisabled &&
    !isReadonly &&
    (assistiveText || helpSourceLink || (messages && messages.length > 0)) &&
    (userAssistanceDensity === 'efficient' || userAssistanceDensity === 'reflow') ? (
      <FormFieldContext.Provider value={{ isFocused: true }}>
        <div class={userAssistanceStyles}>
          <InlineUserAssistance
            assistiveText={assistiveText}
            fieldLabel={label}
            helpSourceLink={helpSourceLink}
            helpSourceText={helpSourceText}
            messages={messages}
            userAssistanceDensity={userAssistanceDensity}
          />
        </div>
      </FormFieldContext.Provider>
    ) : undefined;

  const contentTestId = testId ? testId + (shouldShowSheet ? '_sheet' : '_dropdown') : undefined;

  // The same content is displayed either in a dropdown or sheet: color picker and inline UA.
  // Add styling to the dropdown to enforce a fixed width.
  const dropdownContent = (
    <div class={dropdownStyles}>
      <SectionedContent
        paddingBlockEnd="3.5x"
        paddingBlockStart="3.5x"
        paddingInlineEnd="3.5x"
        paddingInlineStart="3.5x"
        aria-label={colorPickerAriaLabel}
        footer={inlineUserAssistanceFooter}
        hasFocusTrap={true}
        main={colorPickerMainContent}
        role="dialog"
        testId={contentTestId}
      />
    </div>
  );

  const dropdown = shouldShowSheet ? (
    <Sheet isOpen={isOpen} onClose={onClose}>
      <SectionedContent
        aria-label={colorPickerAriaLabel}
        footer={inlineUserAssistanceFooter}
        hasFocusTrap={true}
        main={colorPickerMainContent}
        role="dialog"
        testId={contentTestId}
      />
    </Sheet>
  ) : (
    <StickyPositioningDropdown
      anchorRef={anchorRef}
      isOpen={isOpen}
      onClose={onClose}
      onPosition={handleDropdownPosition}
      sizeOptions={sizeOptions}>
      {dropdownContent}
    </StickyPositioningDropdown>
  );

  return dropdown;
};

const isPhone = () => {
  return getClientHints().deviceType === 'phone';
};

export { InputColorPickerDropdown, type CloseDetail };
